import { config } from "../config";
import { put, call, all, takeLatest } from "redux-saga/effects";
import { SILENT_AUTH_CHECK, showNotification } from "@/legacy/redux";
import { get } from "lodash";
import { setIn } from "./setIn";
import { useSelector } from "react-redux";
import { handleTenancyApiError, requireActiveTenancy } from "@/features/tenancy";

/**
 * @deprecated We don't want to be using this in future. See this file for comment.
 */
export const sessionCall = (modelId, releaseId, session, payload) => ({
  type: "DCSVLY/SESSIONCALL",
  release: releaseId,
  model: modelId,
  payload,
  session,
});

const host = config.BACKEND_URL;

export const SESSION_CALL = "DCSVLY/SESSIONCALL";

/**
 * @deprecated We don't want to be using redux and sagas in future. We will need to build a replacement for this.
 * 
 * Right now this is used by all of the debugger modes to make calls to the backend to create and update sessions.
 * We want this to mostly be driven by the interview sdk in future. This is probably a prime usage to use the sdk
 * directly instead of the react/ui layer.
 */
function* handleSessionCall(authProvider, action) {
  // Call the remote back API
  try {
    const validAuth = yield call(authProvider, "validAuth");
    if (!validAuth) {
      yield put({
        type: SILENT_AUTH_CHECK,
        forceLogin: true,
      });

      return false;
    }

    let headers = {
      "Content-Type": "application/json",
    };
    headers = authProvider("applyAuthentication", headers);
    headers["X-TENANCY"] = requireActiveTenancy();

    if (!action.model || !action.release) throw { message: 'Invalid action' };
    let res;
    if (!action.session) {
      // We need to create a session
      yield put({
        type: "DCSVLY/SESSIONCALL_CREATE",
        release: action.release,
        model: action.model,
      });
      const uri = `${host}/decisionapi/session/${action.model}/${action.release}`;
      res = yield fetch(uri, {
        method: "POST",
        headers,
        body: JSON.stringify(action.payload),
      });
    } else {
      // Continuing a session
      yield put({
        type: "DCSVLY/SESSIONCALL_RESUME",
        release: action.release,
        session: action.session,
        model: action.model,
      });
      const uri = `${host}/decisionapi/session/${action.model}/${action.release}?session=${action.session}`;
      res = yield fetch(uri, {
        method: "PATCH",
        headers,
        body: JSON.stringify(action.payload),
      });
    }
    const payload = yield res.json();
    if (!res.ok) {
      handleTenancyApiError(payload);
      throw payload;
    }
    yield put({
      type: "DCSVLY/SESSIONCALL_SUCCESS",
      release: action.release,
      session: payload.sessionId || payload.session,
      model: action.model,
      payload,
    });

    /*yield put({
      type: "DCSVLY/SESSIONCALL_END",
      release: action.release,
      session: payload.sessionId,
      model: action.model
    });*/
  } catch (error) {
    if (error.message) {
      yield put(showNotification(error.message, "error"));
    }
    yield put({
      type: "DCSVLY/SESSIONCALL_ERROR",
      release: action.release,
      session: action.session,
      model: action.model,
      error: error.message || "Something went wrong",
    });
  }
}

const defaultSession = {};

export const useSessionHook = (model, release, session) => {
  return useSelector((state) => {
    if (!session) {
      const latestSessionId = get(state, `session[${model}][${release}].NEW_SESSION.lastId`);

      return get(state, `session[${model}][${release}][${latestSessionId}]`, defaultSession);
    }

    return get(state, `session[${model}][${release}][${session}]`, defaultSession);
  });
};

/** The id of the most recently created session for this model/release, or undefined if none exists yet. */
export const useCurrentSessionId = (model, release) => {
  return useSelector((state) => get(state, `session[${model}][${release}].NEW_SESSION.lastId`));
};

/** The error message from the most recent failed call for this session, or undefined if the last call succeeded (or none has happened yet). */
export const useSessionError = (model, release, session) => {
  return useSelector((state) => {
    if (!session) {
      const latestSessionId = get(state, `session[${model}][${release}].NEW_SESSION.lastId`);
      return get(state, `session[${model}][${release}][${latestSessionId}].error`);
    }

    return get(state, `session[${model}][${release}][${session}].error`);
  });
};

export const useSessionLoading = (model, release, session) => {
  return useSelector((state) => {
    if (!session) {
      return get(state, `session[${model}][${release}].loading`);
    }

    return get(state, `session[${model}][${release}][${session}].loading`, defaultSession);
  });
};

const defaultSessionState = {};

export const sessionReducer = (previousState = defaultSessionState, action) => {
  let new_state;
  switch (action.type) {
    case "DCSVLY/SESSIONCALL_SUCCESS":
      {
        // Merge (rather than replace) onto any existing record for this session, so a scoped
        // follow-up request (eg. re-fetching just the derived table view) doesn't wipe out
        // fields like `graph`/`report` that weren't part of that particular request.
        const existingRecord = get(previousState, `${action.model}.${action.release}.${action.session}.record`);
        const mergedRecord = existingRecord ? { ...existingRecord, ...action.payload } : action.payload;
        new_state = setIn(previousState, `${action.model}.${action.release}.${action.session}.record`, mergedRecord);
      }
      new_state = setIn(new_state, `${action.model}.${action.release}.${action.session}.loading`, false);
      new_state = setIn(new_state, `${action.model}.${action.release}.${action.session}.error`, undefined);
      new_state = setIn(new_state, `${action.model}.${action.release}.loading`, false);
      new_state = setIn(new_state, `${action.model}.${action.release}.NEW_SESSION.lastId`, action.session);

      return new_state;
    case "DCSVLY/SESSIONCALL_END":
      new_state = setIn(previousState, `${action.model}.${action.release}.${action.session}.loading`, false);
      new_state = setIn(new_state, `${action.model}.${action.release}.loading`, false);
      return new_state;
    case "DCSVLY/SESSIONCALL_ERROR":
      new_state = setIn(previousState, `${action.model}.${action.release}.${action.session}.loading`, false);
      new_state = setIn(new_state, `${action.model}.${action.release}.${action.session}.error`, action.error || true);
      new_state = setIn(new_state, `${action.model}.${action.release}.loading`, false);
      return new_state;
    case "DCSVLY/SESSIONCALL_RESUME":
      new_state = setIn(previousState, `${action.model}.${action.release}.${action.session}.loading`, true);
      new_state = setIn(new_state, `${action.model}.${action.release}.loading`, true);

      return new_state;
    case "DCSVLY/SESSIONCALL_CREATE":
      // Issue with this is there is only NEW_SESSION allowed at a time
      new_state = setIn(previousState, `${action.model}.${action.release}.NEW_SESSION.loading`, true);
      new_state = setIn(new_state, `${action.model}.${action.release}.loading`, true);
      return new_state;

    case "DCSVLY/SESSIONCALL_RESET": return defaultSessionState;

    /* case 'DCSVLY/TESTGEN_SUCCESS':

      new_state = cloneDeep(previousState);
      set(new_state, `${action.release}.${action.id}.record`, {cases: action.cases, attributes: action.attributes} );
      return new_state;
    */
    default:
      return previousState;
  }
};

export function* sessionSaga(authProvider) {
  yield all([
    takeLatest(
      action => action.type === "DCSVLY/SESSIONCALL",
      handleSessionCall,
      authProvider,
    )
  ]);
}
